// Vertex factory functions
// Creates vertex instances with sensible defaults and unique IDs

import type {
  JunctionVertex,
  PressureBoundaryVertex,
  FlowDemandVertex,
  CentrifugalPumpVertex,
  ControlValveVertex,
  TankVertex,
  BalancingOrificeVertex,
  FixedLossVertex,
  ReducerVertex,
  CurveLossVertex,
  BlowerVertex,
  FanVertex,
  CompressorVertex,
  PressureGainVertex,
  PressureReliefValveVertex,
  ThreeWayValveVertex,
  NozzleMeterVertex,
  OrificeMeterVertex,
  VenturiMeterVertex,
  HeatSourceSinkVertex,
  HeatExchangerVertex,
  StrainerVertex,
  CheckValveVertex,
  Vertex,
} from '../vertices';


// ============================================================================
// NEW FACTORY FUNCTIONS (ADR-0009 naming conventions)
// ============================================================================

/**
 * Creates a junction vertex with provided ID and optional overrides
 * @param id - The unique identifier for the junction
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete JunctionVertex object
 */
export function createJunction(
  id: string,
  overrides?: Partial<Omit<JunctionVertex, 'id' | 'type'>>
): JunctionVertex {
  const defaults: JunctionVertex = {
    id,
    type: 'junction',
    name: `Junction ${id}`,
    elevation: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge diagram to preserve structure when partially overriding
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'junction',
  };
}

/**
 * Creates a pressure boundary vertex with provided ID and optional overrides
 * @param id - The unique identifier for the pressure boundary
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete PressureBoundaryVertex object
 */
export function createPressureBoundary(
  id: string,
  overrides?: Partial<Omit<PressureBoundaryVertex, 'id' | 'type'>>
): PressureBoundaryVertex {
  const defaults: PressureBoundaryVertex = {
    id,
    type: 'pressure_boundary',
    name: `Pressure Boundary ${id}`,
    elevation: { value: 0, unit: 'm' },
    // Default to 0 gauge (1 atm absolute), not 0 absolute — a 0-Pa default
    // renders as a −14.7 psig vacuum under the default gauge reference (B16).
    pressure: { value: 101325, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge diagram to preserve structure when partially overriding
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'pressure_boundary',
  };
}

/**
 * Creates a flow demand vertex with provided ID and optional overrides
 * @param id - The unique identifier for the flow demand
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete FlowDemandVertex object
 */
export function createFlowDemand(
  id: string,
  overrides?: Partial<Omit<FlowDemandVertex, 'id' | 'type'>>
): FlowDemandVertex {
  const defaults: FlowDemandVertex = {
    id,
    type: 'flow_demand',
    name: `Flow Demand ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation: { mode: 'flow_out', value: 0, unit: 'm³/s' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge nested objects to preserve structure when partially overriding
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'flow_demand',
  };
}

/**
 * Creates a centrifugal pump vertex with provided ID and optional overrides
 * @param id - The unique identifier for the centrifugal pump
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete CentrifugalPumpVertex object
 */
export function createCentrifugalPump(
  id: string,
  overrides?: Partial<Omit<CentrifugalPumpVertex, 'id' | 'type'>>
): CentrifugalPumpVertex {
  const defaults: CentrifugalPumpVertex = {
    id,
    type: 'centrifugal_pump',
    name: `Centrifugal Pump ${id}`,
    suction_elevation: { value: 0, unit: 'm' },
    discharge_elevation: { value: 0, unit: 'm' },
    operation: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge nested objects to preserve structure when partially overriding
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'centrifugal_pump',
  };
}

/**
 * Creates a control valve vertex with provided ID and optional overrides
 * @param id - The unique identifier for the control valve
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete ControlValveVertex object
 */
export function createControlValve(
  id: string,
  overrides?: Partial<Omit<ControlValveVertex, 'id' | 'type'>>
): ControlValveVertex {
  const defaults: ControlValveVertex = {
    id,
    type: 'control_valve',
    name: `Control Valve ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge nested objects to preserve structure when partially overriding
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'control_valve',
  };
}

/**
 * Creates a tank vertex with provided ID and optional overrides
 * @param id - The unique identifier for the tank
 * @param overrides - Optional partial overrides (id and type cannot be overridden)
 * @returns A complete TankVertex object
 */
export function createTank(
  id: string,
  overrides?: Partial<Omit<TankVertex, 'id' | 'type'>>
): TankVertex {
  const defaults: TankVertex = {
    id,
    type: 'tank',
    name: `Tank ${id}`,
    elevation: { value: 0, unit: 'm' },
    surface_pressure: { value: 101325, unit: 'Pa' },
    liquid_level: { value: 0, unit: 'm' },
    penetrations: [],
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    // Deep merge diagram to preserve structure when partially overriding
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    // Ensure id and type cannot be overridden
    id,
    type: 'tank',
  };
}

// ============================================================================
// v2 FACTORY FUNCTIONS
// ============================================================================

/**
 * Creates a balancing orifice vertex with provided ID and optional overrides
 */
export function createBalancingOrifice(
  id: string,
  overrides?: Partial<Omit<BalancingOrificeVertex, 'id' | 'type'>>
): BalancingOrificeVertex {
  const defaults: BalancingOrificeVertex = {
    id,
    type: 'balancing_orifice',
    name: `Balancing Orifice ${id}`,
    elevation: { value: 0, unit: 'm' },
    meter_diameter: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'balancing_orifice',
  };
}

/**
 * Creates a fixed loss vertex with provided ID and optional overrides
 */
export function createFixedLoss(
  id: string,
  overrides?: Partial<Omit<FixedLossVertex, 'id' | 'type'>>
): FixedLossVertex {
  const defaults: FixedLossVertex = {
    id,
    type: 'fixed_loss',
    name: `Fixed Loss ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    loss: { value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'fixed_loss',
  };
}

/**
 * Creates a strainer vertex — a velocity-dependent Cv loss element.
 * Default loss is a NON-lossless Cv (unlike createFixedLoss's {value:0} trap).
 * cv:25 is a flow-blind placeholder (~1.4 psi @ 30 GPM; ~400 psi @ 500 GPM) —
 * callers should size Cv to the connected line/flow.
 */
export function createStrainer(
  id: string,
  overrides?: Partial<Omit<StrainerVertex, 'id' | 'type'>>,
): StrainerVertex {
  const defaults: StrainerVertex = {
    id,
    type: 'strainer',
    name: `Strainer ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    loss: { mode: 'cv', cv: 25 },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'strainer',
  };
}

/**
 * Creates a check valve vertex — a non-return Cv/K loss element with a spring cracking pressure.
 * Default is a NON-lossless Cv with a ~1 psi (6895 Pa) cracking pressure. Imported Desktop check
 * valves must instead pass `cracking_pressure:{value:0}` (Desktop has no spring). cv:25 is a
 * flow-blind placeholder — callers should size Cv to the line/flow.
 */
export function createCheckValve(
  id: string,
  overrides?: Partial<Omit<CheckValveVertex, 'id' | 'type'>>,
): CheckValveVertex {
  const defaults: CheckValveVertex = {
    id,
    type: 'check_valve',
    name: `Check Valve ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    loss: { mode: 'cv', cv: 25 },
    cracking_pressure: { value: 6895, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'check_valve',
  };
}

/**
 * Creates a reducer vertex with provided ID and optional overrides
 */
export function createReducer(
  id: string,
  overrides?: Partial<Omit<ReducerVertex, 'id' | 'type'>>
): ReducerVertex {
  const defaults: ReducerVertex = {
    id,
    type: 'reducer',
    name: `Reducer ${id}`,
    elevation: { value: 0, unit: 'm' },
    transition_length: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'reducer',
  };
}

/**
 * Creates a curve loss vertex with provided ID and optional overrides
 */
export function createCurveLoss(
  id: string,
  overrides?: Partial<Omit<CurveLossVertex, 'id' | 'type'>>
): CurveLossVertex {
  const defaults: CurveLossVertex = {
    id,
    type: 'curve_loss',
    name: `Curve Loss ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    pressure_drop_curve: { points: [] },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'curve_loss',
  };
}

/**
 * Creates a blower vertex with provided ID and optional overrides
 */
export function createBlower(
  id: string,
  overrides?: Partial<Omit<BlowerVertex, 'id' | 'type'>>
): BlowerVertex {
  const defaults: BlowerVertex = {
    id,
    type: 'blower',
    name: `Blower ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'blower',
  };
}

/**
 * Creates a fan vertex with provided ID and optional overrides
 */
export function createFan(
  id: string,
  overrides?: Partial<Omit<FanVertex, 'id' | 'type'>>
): FanVertex {
  const defaults: FanVertex = {
    id,
    type: 'fan',
    name: `Fan ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'fan',
  };
}

/**
 * Creates a compressor vertex with provided ID and optional overrides
 */
export function createCompressor(
  id: string,
  overrides?: Partial<Omit<CompressorVertex, 'id' | 'type'>>
): CompressorVertex {
  const defaults: CompressorVertex = {
    id,
    type: 'compressor',
    name: `Compressor ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    operation: overrides?.operation
      ? { ...defaults.operation, ...overrides.operation }
      : defaults.operation,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'compressor',
  };
}

/**
 * Creates a pressure gain vertex with provided ID and optional overrides
 */
export function createPressureGain(
  id: string,
  overrides?: Partial<Omit<PressureGainVertex, 'id' | 'type'>>
): PressureGainVertex {
  const defaults: PressureGainVertex = {
    id,
    type: 'pressure_gain',
    name: `Pressure Gain ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    pressure_drop_curve: { points: [] },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'pressure_gain',
  };
}

/**
 * Creates a pressure relief valve vertex with provided ID and optional overrides
 */
export function createPressureReliefValve(
  id: string,
  overrides?: Partial<Omit<PressureReliefValveVertex, 'id' | 'type'>>
): PressureReliefValveVertex {
  const defaults: PressureReliefValveVertex = {
    id,
    type: 'pressure_relief_valve',
    name: `Pressure Relief Valve ${id}`,
    elevation: { value: 0, unit: 'm' },
    set_pressure: { value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'pressure_relief_valve',
  };
}

/**
 * Creates a three-way valve vertex with provided ID and optional overrides
 */
export function createThreeWayValve(
  id: string,
  overrides?: Partial<Omit<ThreeWayValveVertex, 'id' | 'type'>>
): ThreeWayValveVertex {
  const defaults: ThreeWayValveVertex = {
    id,
    type: 'three_way_valve',
    name: `Three Way Valve ${id}`,
    elevation: { value: 0, unit: 'm' },
    operation_port_a: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    operation_port_b: { mode: 'pressure_differential', value: 0, unit: 'Pa' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    operation_port_a: overrides?.operation_port_a
      ? { ...defaults.operation_port_a, ...overrides.operation_port_a }
      : defaults.operation_port_a,
    operation_port_b: overrides?.operation_port_b
      ? { ...defaults.operation_port_b, ...overrides.operation_port_b }
      : defaults.operation_port_b,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'three_way_valve',
  };
}

/**
 * Creates a nozzle meter vertex with provided ID and optional overrides
 */
export function createNozzleMeter(
  id: string,
  overrides?: Partial<Omit<NozzleMeterVertex, 'id' | 'type'>>
): NozzleMeterVertex {
  const defaults: NozzleMeterVertex = {
    id,
    type: 'nozzle_meter',
    name: `Nozzle Meter ${id}`,
    elevation: { value: 0, unit: 'm' },
    nozzle_type: 'long_radius',
    meter_diameter: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'nozzle_meter',
  };
}

/**
 * Creates an orifice meter vertex with provided ID and optional overrides
 */
export function createOrificeMeter(
  id: string,
  overrides?: Partial<Omit<OrificeMeterVertex, 'id' | 'type'>>
): OrificeMeterVertex {
  const defaults: OrificeMeterVertex = {
    id,
    type: 'orifice_meter',
    name: `Orifice Meter ${id}`,
    elevation: { value: 0, unit: 'm' },
    pressure_taps: 'corner',
    meter_diameter: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'orifice_meter',
  };
}

/**
 * Creates a venturi meter vertex with provided ID and optional overrides
 */
export function createVenturiMeter(
  id: string,
  overrides?: Partial<Omit<VenturiMeterVertex, 'id' | 'type'>>
): VenturiMeterVertex {
  const defaults: VenturiMeterVertex = {
    id,
    type: 'venturi_meter',
    name: `Venturi Meter ${id}`,
    elevation: { value: 0, unit: 'm' },
    mfg_method: 'machined',
    divergence_angle: 'seven_degrees',
    meter_diameter: { value: 0, unit: 'm' },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'venturi_meter',
  };
}

/**
 * Creates a heat source/sink vertex with provided ID and optional overrides
 */
export function createHeatSourceSink(
  id: string,
  overrides?: Partial<Omit<HeatSourceSinkVertex, 'id' | 'type'>>
): HeatSourceSinkVertex {
  const defaults: HeatSourceSinkVertex = {
    id,
    type: 'heat_source_sink',
    name: `Heat Source/Sink ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    pressure_drop_curve: { points: [] },
    thermal_calculation: { mode: 'none', is_flow_rate_user_entered: null },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'heat_source_sink',
  };
}


/**
 * Creates a heat exchanger vertex with provided ID and optional overrides
 */
export function createHeatExchanger(
  id: string,
  overrides?: Partial<Omit<HeatExchangerVertex, 'id' | 'type'>>
): HeatExchangerVertex {
  const defaults: HeatExchangerVertex = {
    id,
    type: 'heat_exchanger',
    name: `Heat Exchanger ${id}`,
    inlet_elevation: { value: 0, unit: 'm' },
    outlet_elevation: { value: 0, unit: 'm' },
    pressure_drop_curve: { points: [] },
    hx_configuration: { configuration_type: 'no_hx_configuration' },
    thermal_calculation: {
      modeled_side: { mode: 'none', is_flow_rate_user_entered: null },
      unmodeled_side: { mode: 'none', is_flow_rate_user_entered: null },
    },
    diagram: { position: { x: 0, y: 0 } },
  };

  return {
    ...defaults,
    ...overrides,
    diagram: overrides?.diagram
      ? { ...defaults.diagram, ...overrides.diagram }
      : defaults.diagram,
    id,
    type: 'heat_exchanger',
  };
}


/**
 * Build a fully-defaulted vertex of `type` at `position`, via that type's factory.
 *
 * Every device the palette can add must arrive CALCULABLE — a bare `{id, type,
 * name}` vertex omits schema-required fields (a pressure boundary with no
 * `pressure`, a strainer with no `loss`) and makes calculate throw. The factories
 * already carry sensible physical defaults (pressure boundary → 1 atm, etc.); this
 * dispatcher is the single door the creation UI uses instead of hand-rolling a
 * partial vertex.
 *
 * Throws on an unmapped type — a NEW addable type without a factory should fail
 * loudly here, not silently create an uncalculatable device (no silent fallback).
 */
export function defaultVertexForType(
  type: string,
  id: string,
  position: { x: number; y: number },
): Vertex {
  const o = { diagram: { position } };
  switch (type) {
    case 'pressure_boundary': return createPressureBoundary(id, o);
    case 'tank': return createTank(id, o);
    case 'centrifugal_pump': return createCentrifugalPump(id, o);
    case 'compressor': return createCompressor(id, o);
    case 'fan': return createFan(id, o);
    case 'blower': return createBlower(id, o);
    case 'control_valve': return createControlValve(id, o);
    case 'three_way_valve': return createThreeWayValve(id, o);
    case 'pressure_relief_valve': return createPressureReliefValve(id, o);
    case 'junction': return createJunction(id, o);
    case 'curve_loss': return createCurveLoss(id, o);
    case 'fixed_loss': return createFixedLoss(id, o);
    case 'strainer': return createStrainer(id, o);
    case 'check_valve': return createCheckValve(id, o);
    case 'reducer': return createReducer(id, o);
    case 'heat_exchanger': return createHeatExchanger(id, o);
    case 'heat_source_sink': return createHeatSourceSink(id, o);
    case 'balancing_orifice': return createBalancingOrifice(id, o);
    case 'pressure_gain': return createPressureGain(id, o);
    case 'flow_demand': return createFlowDemand(id, o);
    case 'orifice_meter': return createOrificeMeter(id, o);
    case 'venturi_meter': return createVenturiMeter(id, o);
    case 'nozzle_meter': return createNozzleMeter(id, o);
    // PIPE-46: 'tee' and 'bend' are RETIRED — no case here, so asking for one
    // hits the throw below. Their factories are gone too; only the Zod schemas
    // remain, so vertices already saved in customer models still parse. Do not
    // re-add a case without also restoring the frontend registry entry to
    // `addable: true` — the two dispatchers must stay in step.
    default:
      throw new Error(`defaultVertexForType: no factory for vertex type '${type}'`);
  }
}
